// Cómo se llaman en pantalla las cifras que este módulo sabe medir.
//
// Aparte del resto de los textos por un motivo que no es de tamaño: estas claves
// son un CONTRATO con el catálogo de analítica (`backend/analytics.js`), no copia
// de una pantalla. Cada `labelKey` que aquel fichero declara tiene aquí su
// traducción en los cuatro idiomas, y una que falte no rompe nada — sale el id
// hecho legible, en inglés, en medio de una pantalla en castellano.
//
// La ayuda de un dataset (`help`) es lo que alguien lee en el desplegable de
// Explorar antes de elegirlo. Dice qué es UNA FILA, que es la pregunta que de
// verdad distingue dos conjuntos de datos parecidos.

const analytics = {
  es: {
    orders: {
      label: 'Órdenes de fabricación',
      help: 'Una fila por orden de fabricación. Las anuladas quedan fuera',
    },
    dates: {
      plannedEnd: 'Fecha de salida prevista',
      plannedStart: 'Inicio previsto',
      started: 'Inicio real',
      completed: 'Fecha de fin',
      created: 'Fecha de la orden',
    },
    metrics: {
      orders: 'Órdenes',
      open: 'Pendientes',
      wip: 'En curso',
      completed: 'Terminadas',
      late: 'Fuera de plazo',
      onTime: 'Terminadas a tiempo',
      onTimeRate: 'Cumplimiento de fechas',
      leadTime: 'Tiempo de fabricación',
      workedHours: 'Horas reales',
      estimatedHours: 'Horas estimadas',
      hoursDeviation: 'Desviación de horas',
      actualMaterial: 'Material consumido',
      estimatedMaterial: 'Material previsto',
      materialDeviation: 'Desviación de material',
      quantity: 'Cantidad pedida',
      produced: 'Cantidad producida',
      yield: 'Rendimiento',
    },
    dimensions: {
      status: 'Estado',
      bom: 'Producto',
      assignee: 'Asignada a',
      project: 'Obra',
      priority: 'Prioridad',
      unit: 'Unidad',
    },
    filters: {
      quantity: 'Cantidad pedida',
    },
  },
  ca: {
    orders: {
      label: 'Ordres de fabricació',
      help: 'Una fila per ordre de fabricació. Les anul·lades queden fora',
    },
    dates: {
      plannedEnd: 'Data de sortida prevista',
      plannedStart: 'Inici previst',
      started: 'Inici real',
      completed: 'Data de fi',
      created: 'Data de l\'ordre',
    },
    metrics: {
      orders: 'Ordres',
      open: 'Pendents',
      wip: 'En curs',
      completed: 'Acabades',
      late: 'Fora de termini',
      onTime: 'Acabades a temps',
      onTimeRate: 'Compliment de dates',
      leadTime: 'Temps de fabricació',
      workedHours: 'Hores reals',
      estimatedHours: 'Hores estimades',
      hoursDeviation: 'Desviació d\'hores',
      actualMaterial: 'Material consumit',
      estimatedMaterial: 'Material previst',
      materialDeviation: 'Desviació de material',
      quantity: 'Quantitat demanada',
      produced: 'Quantitat produïda',
      yield: 'Rendiment',
    },
    dimensions: {
      status: 'Estat',
      bom: 'Producte',
      assignee: 'Assignada a',
      project: 'Obra',
      priority: 'Prioritat',
      unit: 'Unitat',
    },
    filters: {
      quantity: 'Quantitat demanada',
    },
  },
  en: {
    orders: {
      label: 'Manufacturing orders',
      help: 'One row per manufacturing order. Cancelled ones are excluded',
    },
    dates: {
      plannedEnd: 'Planned end',
      plannedStart: 'Planned start',
      started: 'Started',
      completed: 'Completed',
      created: 'Order raised',
    },
    metrics: {
      orders: 'Orders',
      open: 'Open',
      wip: 'In progress',
      completed: 'Completed',
      late: 'Late',
      onTime: 'Finished on time',
      onTimeRate: 'On-time rate',
      leadTime: 'Lead time',
      workedHours: 'Worked hours',
      estimatedHours: 'Estimated hours',
      hoursDeviation: 'Hours deviation',
      actualMaterial: 'Material used',
      estimatedMaterial: 'Material planned',
      materialDeviation: 'Material deviation',
      quantity: 'Quantity ordered',
      produced: 'Quantity produced',
      yield: 'Yield',
    },
    dimensions: {
      status: 'Status',
      bom: 'Product',
      assignee: 'Assigned to',
      project: 'Job',
      priority: 'Priority',
      unit: 'Unit',
    },
    filters: {
      quantity: 'Quantity ordered',
    },
  },
  fr: {
    orders: {
      label: 'Ordres de fabrication',
      help: 'Une ligne par ordre de fabrication. Les annulés sont exclus',
    },
    dates: {
      plannedEnd: 'Fin prévue',
      plannedStart: 'Début prévu',
      started: 'Début réel',
      completed: 'Date de fin',
      created: 'Date de l\'ordre',
    },
    metrics: {
      orders: 'Ordres',
      open: 'En cours',
      wip: 'En fabrication',
      completed: 'Terminés',
      late: 'En retard',
      onTime: 'Terminés à temps',
      onTimeRate: 'Respect des délais',
      leadTime: 'Temps de fabrication',
      workedHours: 'Heures réelles',
      estimatedHours: 'Heures estimées',
      hoursDeviation: 'Écart d\'heures',
      actualMaterial: 'Matériau consommé',
      estimatedMaterial: 'Matériau prévu',
      materialDeviation: 'Écart de matériau',
      quantity: 'Quantité commandée',
      produced: 'Quantité produite',
      yield: 'Rendement',
    },
    dimensions: {
      status: 'Statut',
      bom: 'Produit',
      assignee: 'Assigné à',
      project: 'Chantier',
      priority: 'Priorité',
      unit: 'Unité',
    },
    filters: {
      quantity: 'Quantité commandée',
    },
  },
}

/**
 * Adds the analytics vocabulary to the module's own catalogue, locale by locale.
 *
 * Merged here rather than pasted into the big literal so the block stays
 * readable and so adding a metric is one line in two files instead of four
 * edits in the middle of eight hundred.
 */
export function withAnalytics(base, namespace) {
  const merged = {}
  for (const [locale, catalogue] of Object.entries(base)) {
    merged[locale] = {
      ...catalogue,
      [namespace]: { ...catalogue[namespace], analytics: analytics[locale] },
    }
  }
  return merged
}

export default analytics
